import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDiscoveryClient } from '../api/context';
import type { SoundSuggestionInput, SuggestionApproveInput } from '../api/types';
import { NO_OUTCOMES, type Outcomes, type SuggestionList, type SuggestionQuery } from './model';

export const suggestionsKey = ['search-suggestions'] as const;
const outcomesKey = ['discovery', 'insights', 'outcomes'] as const;

/** One page of suggested searches for the filters in the URL. */
export function useSuggestions(query: SuggestionQuery) {
  const client = useDiscoveryClient();
  return useQuery<SuggestionList>({
    queryKey: [...suggestionsKey, query] as const,
    queryFn: () => client.suggestions.query(query),
    // Keep the rows on screen while a new sort or tab loads.
    placeholderData: keepPreviousData,
  });
}

export function useInvalidateSuggestions(): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: suggestionsKey });
}

export function useApproveSuggestion() {
  const client = useDiscoveryClient();
  const invalidate = useInvalidateSuggestions();
  return useMutation({
    mutationFn: (v: { id: string; input: SuggestionApproveInput }) => client.suggestions.approve(v.id, v.input),
    onSuccess: invalidate,
  });
}

export function useRejectSuggestion() {
  const client = useDiscoveryClient();
  const invalidate = useInvalidateSuggestions();
  return useMutation({
    mutationFn: (id: string) => client.suggestions.reject(id),
    onSuccess: invalidate,
  });
}

type Progress = (done: number, total: number) => void;

export function useBulkApproveSuggestions() {
  const client = useDiscoveryClient();
  const invalidate = useInvalidateSuggestions();
  return useMutation({
    mutationFn: (v: { ids: string[]; input: SuggestionApproveInput; onProgress?: Progress }) =>
      client.suggestions.bulkApprove(v.ids, v.input, v.onProgress),
    // Settled, not success: a partial failure still changed some rows.
    onSettled: invalidate,
  });
}

export function useBulkRejectSuggestions() {
  const client = useDiscoveryClient();
  const invalidate = useInvalidateSuggestions();
  return useMutation({
    mutationFn: (v: { ids: string[]; onProgress?: Progress }) => client.suggestions.bulkReject(v.ids, undefined, v.onProgress),
    onSettled: invalidate,
  });
}

/**
 * Outreach results per folder and per schedule. Never an error state for the
 * caller: the chips it feeds are extra, so a failed or missing endpoint just
 * means no chips.
 */
export function useOutcomes(): Outcomes {
  const client = useDiscoveryClient();
  const { data } = useQuery<Outcomes>({
    queryKey: outcomesKey,
    queryFn: () => client.insights.outcomes(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return data ?? NO_OUTCOMES;
}

/** `schedules.autoPauseAfterDryRuns` from Settings. Null = off, or a server without the setting. */
export function useAutoPauseAfterDryRuns(): number | null {
  const client = useDiscoveryClient();
  const { data } = useQuery({
    // Shared with useArchiveAfterCompleteDays — one request for both.
    queryKey: ['discovery', 'settings', 'values'],
    queryFn: () => client.workspace.settingsValues(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const v = data?.values?.['schedules.autoPauseAfterDryRuns'];
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/** Send one sound to the queue (from a Lookalike analysis). */
export function useSuggestSound() {
  const client = useDiscoveryClient();
  const invalidate = useInvalidateSuggestions();
  return useMutation({
    mutationFn: (input: SoundSuggestionInput) => client.suggestions.fromSound(input),
    onSuccess: invalidate,
  });
}
